import {
  createFileRoute,
  redirect,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { getDefaultStore } from "jotai";
import { ShieldCheckIcon } from "lucide-react";
import { z } from "zod";
import { AuthPage, LoginForm } from "@tubo/ui/components/auth";
import { isAuthenticatedAtom, useAuthActions } from "@tubo/store";
import {
  useSysGetBeianNumbers,
  useSysGetRegistrationStatus,
} from "@tubo/api-models/hooks/system-config-api.hooks";
import type { LoginResp } from "@tubo/api-models/hooks/auth-api.schemas";
import { buildBeianSubFooterLinks } from "@client/lib/beian-links";
import { getLoginNavigationOptions } from "@client/lib/login-default-route";
import { AuthPreferencesMenu } from "@client/components/auth/auth-preferences-menu";
import { useUiTranslation } from "@tubo/ui/i18n";

const branding = {
  logo: "/TuboData-Logo-Light.png",
  tagline: "国内首家依托业务本体建模\n支持本地化交付的产业级 AI 知识库平台",
  brandingBackground: "linear-gradient(150deg, #001a33 0%, #003366 35%, #0055aa 65%, #007aff 100%)",
  footerLinksOnBranding: false,
  footerLinks: [
    {
      label: "隐私协议",
      href: "/privacy-policy",
      icon: <ShieldCheckIcon className="size-3.5" aria-hidden="true" />,
    },
  ],
};

const loginSearchSchema = z.object({
  redirect: z.string().optional(),
});

export const Route = createFileRoute("/login")({
  validateSearch: loginSearchSchema,
  beforeLoad: ({ search }) => {
    if (getDefaultStore().get(isAuthenticatedAtom)) {
      throw redirect(getLoginNavigationOptions(search.redirect ?? "/"));
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const { redirect } = useSearch({ from: "/login" });
  const { t } = useUiTranslation("auth");
  const { setAuth } = useAuthActions();
  // 查询失败时兜底展示注册入口（与后端默认放行一致）
  const { data: registrationData } = useSysGetRegistrationStatus();
  const registrationAllowed = registrationData?.allowed !== false;
  const { data: beianData } = useSysGetBeianNumbers();
  const subFooterLinks = buildBeianSubFooterLinks(beianData);

  const handleSuccess = async (resp: LoginResp) => {
    if (resp.token?.accessToken) {
      setAuth({ token: resp.token.accessToken, me: resp.me });
      // 身份边界 = 运行时边界：整页进入目标路由，上一会话的内存态
      // （Query 缓存/模块级 atom/blob 缓存等）随页面卸载全部蒸发
      window.location.assign(redirect ?? "/");
    }
  };

  return (
    <AuthPage className="relative" branding={{
      ...branding,
      companyName: t("companyName"),
      logoAlt: t("logoAlt"),
      tagline: t("tagline"),
      footerText: t("copyright", {
        year: new Date().getFullYear(),
        company: t("companyName"),
      }),
      footerLinks: branding.footerLinks?.map((link) => ({ ...link, label: t("privacyPolicy") })),
      subFooterLinks,
    }}>
      <div className="absolute right-6 top-6 z-20 md:right-8 md:top-8">
        <AuthPreferencesMenu />
      </div>
      <LoginForm
        onSuccess={handleSuccess}
        onToggleMode={
          registrationAllowed
            ? () => navigate({ to: "/register", search: { redirect } })
            : undefined
        }
      />
    </AuthPage>
  );
}
